import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Components
import { ActionTypes } from '@/components/Store.d';
import { BannerExistingProduct } from '@/components/BannerExistingProduct/BannerExistingProduct';
import { Button } from '@/components/Button/Button';
import { Context } from '@/components/Store';
import { ProductHeading } from '@/components/ProductHeading/ProductHeading';
import { ProductImage } from '@/components/ProductImage/ProductImage';
import { SmartDescription } from '@/components/SmartDescription/SmartDescription';
import { SupporterTabs } from '@/components/SupporterTabs/SupporterTabs';
import Auth from '@/lib/authentication';
// Modules
import { isSupporterTier, supporterRefs } from '@/lib/config';
import { getTiers } from '@/lib/mapping';
import { getProductImage, setMinimumAmount, setSupporterAmount } from '@/lib/util';
import { validateInput } from '@/lib/validator';

type Props = {
  isEligibilityChecked: boolean;
  setDidClickNext: React.Dispatch<React.SetStateAction<boolean>>;
  showActiveProductWarning: boolean;
};

const Supporter = ({ isEligibilityChecked, setDidClickNext, showActiveProductWarning }: Props) => {
  const [state, dispatch] = useContext(Context);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [queryStrings] = useSearchParams();

  useEffect(() => {
    const { period, amount } = { period: queryStrings.get('period'), amount: queryStrings.get('amount') };
    const supporterRef = state.bundle && state.bundle.bundleReference ? supporterRefs[state.bundle.bundleReference as keyof typeof supporterRefs] : 'monthly';

    dispatch({
      type: ActionTypes.initSupporter,
      payload: {
        tier: amount || Object.values(getTiers(supporterRef))[1] || Object.values(getTiers('monthly'))[1] || '',
        period: isSupporterTier(period) ? period : supporterRef,
        tiers: isSupporterTier(period) ? getTiers(period) : getTiers(supporterRef),
        minimumAmount: setMinimumAmount(supporterRef)
      }
    });
  }, [dispatch, state.bundle, queryStrings]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);

    setDidClickNext(true);

    const customValue = document.querySelector<HTMLInputElement>('[name=customValue]');

    if (customValue && validateInput(customValue)) {
      dispatch({
        type: ActionTypes.disableForm,
        payload: {
          key: 'disableOnSubmit',
          value: true
        }
      });

      // See Product-Komponent for Event Handler
      await Auth.signIn();
    }
  };

  const productName = state?.bundle?.name ? state.bundle.name : '';

  const productImage = state && state.bundle ? getProductImage(state.bundle) : null;

  return (
    <>
      {productImage ? (
        <ProductImage
          productImage={productImage}
          productName={productName}
        />
      ) : null}
      {productName ? <ProductHeading productName={productName} /> : null}

      <div className="product-content">
        {state?.bundle?.description ? <SmartDescription /> : null}

        {showActiveProductWarning ? (
          <BannerExistingProduct />
        ) : (
          <form
            className="form-supporter"
            name="form"
            onSubmit={(e) => void handleSubmit(e)}
          >
            <SupporterTabs />
            <p className="price">
              Ihr ausgewählter Betrag:&nbsp;
              <strong>{setSupporterAmount(state.supporter.selection)}</strong>
            </p>

            <Button
              ariaLabel="Weiter"
              className={['form--button-cta', !isEligibilityChecked || isSubmitted ? 'loading' : ''].join(' ')}
              disabled={!isEligibilityChecked || isSubmitted}
              type="submit"
            >
              Weiter
            </Button>
          </form>
        )}
      </div>
    </>
  );
};

export { Supporter };
